import React, { useState } from 'react'
import { Label } from '../../../../components/Label'
import { SequenceTypeIcon } from '../../components/SequenceTypeIcon'
import { Avatar, Card, Grid, IconButton, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../../../services/api'
import { ToAssignAthleteModal } from '../../Atlhetes/ToAssignAthleteModal'
import moment from 'moment'
import { DialogConfirmation } from '../../../../components/DialogConfirmation'
import Iconify from '../../../../components/Iconify'
import { useTheme } from '@mui/material/styles'
import { setLimitCharacter } from 'utils'
import useResponsive from 'hooks/useResponsive'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { Routine } from 'models/interfaces/routine.interface'

type ProfessionalAthleteProps = {
  professionalAthleteId: number
  name: string
  startedAt: string
  dueDateAt: string
  visibleAfterDueDate: boolean
}

type Props = {
  items: Routine[]
  onEdit: any
}

export const RoutineDataList = ({ items, onEdit }: Props) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(null)
  const [routine, setRoutine] = useState<any | null>(null)
  const [indexRoutine, setIndexRoutine] = useState<any>()
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const toggleDialog = () => setOpenConfirmation(!openConfirmation)
  const [routineToAssign, setRoutineToAssign] = useState<any>()
  const [professionalAthletesToAssign, setProfessionalAthletesToAssign] = useState<any>([])
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [loading] = useState<boolean>(false)
  const mdUp = useResponsive('up', 'md')

  const handleOpenMenu = (event: any, routine: any, index: number) => {
    setOpen(event.currentTarget)
    setRoutine(routine)
    setIndexRoutine(index)
  }

  const handleCloseMenu = () => {
    setOpen(null)
  }

  const handleGoToRoutine = (item: Routine) => {
    navigate(`/routine/${item.id}`, { state: item })
  }

  const handleRemove = async () => {
    try {
      await api.delete(`routines/${routine?.id}`)
      dispatch(showMessage({ message: `${t('routine.delete_success')}` }))
      items.splice(indexRoutine, 1)
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
    (items.length === 0 ? navigate('/routine', { state: { showInstruction: true } }) : setOpenConfirmation(false))
  }

  const showToAthlete = async () => {
    setRoutineToAssign(routine.id)
    const startedAt = new Date()
    const dueDateAt = new Date()
    dueDateAt.setMonth(dueDateAt.getMonth() + 1)
    try {
      const { data } = await api.get('professional-athletes?active=true')
      const professionalAthleteResult: any = []
      data.forEach((value: any) => {
        professionalAthleteResult.push({
          id: value.id,
          athleteName: value.athlete.name,
          athleteSurname: value.athlete.surname,
          athleteEmail: value.athlete.email,
          athleteImagePath: (value.athlete.imagePath ? value.athlete.imagePath : ''),
          routineId: routine.id,
          name: routine.name,
          note: routine.note,
          startedAt: moment(startedAt).format('DD/MM/YYYY'),
          dueDateAt: moment(dueDateAt).format('DD/MM/YYYY'),
          visibleAfterDueDate: false
        })
      })
      setProfessionalAthletesToAssign(professionalAthleteResult)
      setIsVisible(true)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const handleAssignRoutineToAthlete = async (professionalAthletesToAssign: ProfessionalAthleteProps[]): Promise<void> => {
    if (professionalAthletesToAssign.length === 0) {
      dispatch(showMessage({ message: `${t('routine.professionalAthleteEmpty')}`, type: 'error' }))
    } else {
      try {
        const result = await api.post(`/routines/${routineToAssign}/import-professional-athlete`,
          { professionalAthleteRoutineParams: professionalAthletesToAssign },
          { headers: { 'Content-Type': 'application/json' } })
        if (result) {
          setIsVisible(false)
          setRoutineToAssign(null)
          setProfessionalAthletesToAssign([])
          dispatch(showMessage({ message: `${t('routine.assigned_success')}` }))
        }
      } catch (e) {
        dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
      }
    }
  }

  return (
    <>
      <DialogConfirmation
        title={`${t('routine.deleteConfirmation_title')}`}
        message={`${t('routine.deleteConfirmation_messenger', { name: routine?.name })}`}
        open={openConfirmation}
        onClose={toggleDialog}
        action={handleRemove}
      />
      <ToAssignAthleteModal
        professionalAthletesToAssign={professionalAthletesToAssign}
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        loading={loading}
        handleAssignRoutineToAthlete={handleAssignRoutineToAthlete}
      />
      { items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((item: any, index: any) => (
        <Card key={index} sx={{ pl: '10px', mb: '10px' }}>
          <Grid padding={1} key={`container_${index}`} container alignItems="center">
            <Grid item xs={10} md={10.7} sx={{ cursor: 'pointer' }} onClick={() => { handleGoToRoutine(item) }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack>
                  <Avatar sx={{ width: '60px', height: '60px', bgcolor: theme.palette.primary.main }}>
                    <Iconify color="#FFF" icon="arcticons:home-workouts" width="40px"></Iconify>
                  </Avatar>
                </Stack>
                <Stack spacing={1} padding={1}>
                  <Stack spacing={1} direction="row">
                    <SequenceTypeIcon type={item.type} />
                    <Typography textAlign={'justify'} variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                      {item.name} - {t(`routine.level.${item?.level.toLowerCase()}`)}
                    </Typography>
                  </Stack>
                  <Stack spacing={1} direction="row">
                    <Tooltip arrow placement='top' title={`${item.note}`}>
                      <Stack sx={{ textAlign: 'justify' }} marginTop={0.7} >
                        <Typography variant="body2">
                          { setLimitCharacter(item.note, (mdUp ? 450 : 95)) }
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                  <Stack spacing={1} direction="row">
                    { mdUp && <Grid key={`labels_${index}`} container direction="row">
                      {item.labels.map(({ name, id }: any, key: any) =>
                        <Stack padding={0.4} key={key}>
                          <Label name={name} id={id} color="primary" />
                        </Stack>
                      )}
                    </Grid> }
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={2} md={1.3} sx={{ cursor: 'normal' }} textAlign={'center'}>
              <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, item, index)}>
                <Iconify icon={'eva:more-vertical-fill'} />
              </IconButton>
            </Grid>
          </Grid>
        </Card>
      ))}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75
            }
          }
        }}
      >
        <MenuItem onClick={async () => {
          handleCloseMenu()
          await showToAthlete()
        }}>
          <Iconify icon={'uil:import'} sx={{ mr: 2 }} />
          {t('routine.action.assignToAthlete')}
        </MenuItem>

        <MenuItem onClick={() => {
          handleCloseMenu()
          onEdit(routine)
        }}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          {t('general.btn.edit')}
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={() => {
          handleCloseMenu()
          toggleDialog()
        }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          {t('general.btn.delete')}
        </MenuItem>
      </Popover>
    </>
  )
}
